import { Table } from "@tanstack/react-table";
import { useEffect } from "react";

import { generatePaginationLinks } from "./generate-pages";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HUNDRED,
  HUNDRED_FIFTY,
  TEN,
  TWENTY_FIVE,
} from "@/constants/constants";
import { ResponsePagination } from "@/hooks/usePagination";

type PaginatorProps<TData> = {
  table: Table<TData>;
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  showPreviousNext: boolean;
  pageSizeOptions?: number[];
  pagination?: ResponsePagination;
};

const pageSizes = [TEN, TWENTY_FIVE, HUNDRED, HUNDRED_FIFTY];

const Paginator = <TData,>({
  table,
  currentPage,
  totalPages,
  onPageChange,
  showPreviousNext,
  pageSizeOptions = pageSizes,
  pagination,
}: PaginatorProps<TData>): React.JSX.Element => {
  // this is for initial page size or limit

  useEffect(() => {
    if (!pageSizeOptions.includes(table.getState().pagination.pageSize)) {
      table.setPageSize(pageSizeOptions[10]);
    }
  }, [pageSizeOptions, table]);

  useEffect(() => {
    if (table) {
      table.setPageSize(pagination?.limit ?? 10);
    }
  }, [pagination, table]);

  return (
    <div className='flex items-center gap-4'>
      <div className='flex items-center space-x-2'>
        <p className='text-sm font-medium whitespace-nowrap'>Show</p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            pagination?.handleLimitUpdate(Number(value));
            table.setPageSize(Number(value));
          }}
        >
          <SelectTrigger className='h-8 w-[5rem]'>
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent side='top'>
            {pageSizeOptions.map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize ?? pagination?.limit}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className='text-sm font-medium whitespace-nowrap'>Entries</p>
      </div>
      <Pagination>
        <PaginationContent>
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage - 1 < 1}
              />
            </PaginationItem>
          ) : null}
          {generatePaginationLinks(currentPage, totalPages, onPageChange)}
          {showPreviousNext && totalPages ? (
            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage > totalPages - 1}
              />
            </PaginationItem>
          ) : null}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default Paginator;
